import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getPageTitleSizeClass, getSectionHeadingSizeClass, getTextSizeClass } from '../../utils/fontSizeUtils';

const FUNDAMENTAL_VISUAL_CLASS =
  'relative h-14 w-20 rounded-md overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 border border-slate-200/80 dark:border-slate-600/50';

function FundamentalIndicatorVisual({ slug }: { slug: string }) {
  const w = 80;
  const h = 56;
  const pad = 6;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;

  switch (slug) {
    // Valuation-focused ratio tiles (numerator over denominator)
    case 'pe_ratio': {
      const top = 'Price';
      const bottom = 'EPS';
      const midY = pad + innerH / 2;
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={midY}
              y2={midY}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={midY - 8}
              textAnchor="middle"
              fontSize="18"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={midY + 18}
              textAnchor="middle"
              fontSize="18"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }
    case 'forward_pe_ratio': {
      const top = 'Price';
      const bottom = 'Fwd EPS';
      const midY = pad + innerH / 2;
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={midY}
              y2={midY}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={midY - 8}
              textAnchor="middle"
              fontSize="18"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={midY + 18}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }
    case 'peg_ratio': {
      const top = 'P/E';
      const bottom = 'Growth';
      const midY = pad + innerH / 2;
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={midY}
              y2={midY}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={midY - 8}
              textAnchor="middle"
              fontSize="17"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={midY + 18}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }
    case 'ps_ratio': {
      const top = 'Price';
      const bottom = 'Sales';
      const midY = pad + innerH / 2;
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={midY}
              y2={midY}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={midY - 8}
              textAnchor="middle"
              fontSize="18"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={midY + 18}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }
    case 'pb_ratio': {
      const top = 'Price';
      const bottom = 'Book';
      const midY = pad + innerH / 2;
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={midY}
              y2={midY}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={midY - 8}
              textAnchor="middle"
              fontSize="18"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={midY + 18}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }

    // Income / dividend shown as ratio (Div / Price)
    case 'dividend_yield': {
      const top = 'Div';
      const bottom = 'Price';
      const midY = pad + innerH / 2;
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={midY}
              y2={midY}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={midY - 8}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={midY + 18}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }

    // Profitability / returns shown as ratios
    case 'diluted_earnings_per_share_adjusted': {
      const top = 'NI';
      const bottom = 'Shares';
      const midY = pad + innerH / 2;
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={midY}
              y2={midY}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={midY - 8}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={midY + 18}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }
    case 'roa': {
      const top = 'NI';
      const bottom = 'Assets';
      const midY = pad + innerH / 2;
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={midY}
              y2={midY}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={midY - 8}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={midY + 18}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }
    case 'roe': {
      const top = 'NI';
      const bottom = 'Equity';
      const midY = pad + innerH / 2;
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={midY}
              y2={midY}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={midY - 8}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={midY + 18}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }

    // Balance sheet liquidity ratios
    case 'current_ratio': {
      const top = 'CA';
      const bottom = 'CL';
      const midY = pad + innerH / 2;
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={midY}
              y2={midY}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={midY - 8}
              textAnchor="middle"
              fontSize="17"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={midY + 18}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }
    case 'quick_ratio': {
      const top = 'Quick';
      const bottom = 'CL';
      const midY = pad + innerH / 2;
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={midY}
              y2={midY}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={midY - 8}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={midY + 18}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }

    // Book value per share as ratio (Equity / Shares)
    case 'book_value_per_share': {
      const top = 'Equity';
      const bottom = 'Shares';
      const midY = pad + innerH / 2;
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={midY}
              y2={midY}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={midY - 8}
              textAnchor="middle"
              fontSize="15"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={midY + 18}
              textAnchor="middle"
              fontSize="15"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }

    // Cash flow level metrics
    case 'operating_cash_flow':
    case 'investing_cash_flow':
    case 'financing_cash_flow':
    case 'capital_expenditure':
    case 'free_cash_flow': {
      const bars = [0.4, 0.7, 0.3, 0.9];
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad}
              x2={pad + innerW}
              y1={pad + innerH / 2}
              y2={pad + innerH / 2}
              stroke="currentColor"
              strokeWidth={1}
              strokeDasharray="2 2"
              className="text-slate-400 dark:text-slate-500"
            />
            {bars.map((pct, idx) => {
              const barW = innerW / bars.length - 3;
              const x = pad + 2 + idx * (innerW / bars.length);
              const barH = (innerH / 2 - 4) * pct;
              const isPositive = idx % 2 === 0;
              return (
                <rect
                  key={idx}
                  x={x}
                  y={isPositive ? pad + innerH / 2 - barH : pad + innerH / 2}
                  width={barW}
                  height={barH}
                  rx={2}
                  fill="currentColor"
                  className={isPositive ? 'text-emerald-500 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'}
                />
              );
            })}
          </svg>
        </div>
      );
    }

    // Cash flow ratios / yields (numerator over denominator)
    case 'cash_flow_margin': {
      const top = 'OCF';
      const bottom = 'Rev';
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={pad + innerH / 2}
              y2={pad + innerH / 2}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={pad + innerH / 2 - 8}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={pad + innerH / 2 + 18}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }
    case 'ocf_ratio': {
      const top = 'OCF';
      const bottom = 'CL';
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={pad + innerH / 2}
              y2={pad + innerH / 2}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={pad + innerH / 2 - 8}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={pad + innerH / 2 + 18}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }
    case 'fcf_yield': {
      const top = 'FCF';
      const bottom = 'MktCap';
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
            <line
              x1={pad + 4}
              x2={pad + innerW - 4}
              y1={pad + innerH / 2}
              y2={pad + innerH / 2}
              stroke="currentColor"
              strokeWidth={1.5}
              className="text-emerald-500 dark:text-emerald-500"
            />
            <text
              x={pad + innerW / 2}
              y={pad + innerH / 2 - 8}
              textAnchor="middle"
              fontSize="16"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {top}
            </text>
            <text
              x={pad + innerW / 2}
              y={pad + innerH / 2 + 18}
              textAnchor="middle"
              fontSize="15"
              className="fill-slate-900 dark:fill-slate-900"
            >
              {bottom}
            </text>
          </svg>
        </div>
      );
    }

    default: {
      // Fallback mini bars
      return (
        <div className={FUNDAMENTAL_VISUAL_CLASS}>
          <div className="absolute inset-2 flex items-end justify-between gap-0.5">
            {[0.4, 0.7, 0.35, 0.9, 0.5].map((pct, i) => (
              <div key={i} className="flex-1 rounded-sm bg-emerald-500/80 dark:bg-emerald-400/80" style={{ height: `${pct * 100}%` }} />
            ))}
          </div>
        </div>
      );
    }
  }
}

interface FundamentalTutorialConfig {
  slug: string;          // also matches indicatorKey in INDICATOR_INFO
  shortLabel: string;
  oneLineSummary: string;
}

const FUNDAMENTAL_TUTORIALS: FundamentalTutorialConfig[] = [
  {
    slug: 'pe_ratio',
    shortLabel: 'P/E Ratio',
    oneLineSummary: 'See how much investors are paying for each dollar of earnings and whether that pricing looks rich or reasonable.',
  },
  {
    slug: 'forward_pe_ratio',
    shortLabel: 'Forward P/E Ratio',
    oneLineSummary: 'Compare today’s price to next year’s expected earnings to understand how much growth the market is pricing in.',
  },
  {
    slug: 'peg_ratio',
    shortLabel: 'PEG Ratio',
    oneLineSummary: 'Balance valuation against expected growth to spot companies that may be expensive or inexpensive for their growth rate.',
  },
  {
    slug: 'ps_ratio',
    shortLabel: 'P/S Ratio',
    oneLineSummary: 'Value companies based on revenue when earnings are volatile or not yet meaningful.',
  },
  {
    slug: 'pb_ratio',
    shortLabel: 'P/B Ratio',
    oneLineSummary: 'Compare the stock price to accounting book value to see whether shares trade above or below net assets.',
  },
  {
    slug: 'dividend_yield',
    shortLabel: 'Dividend Yield',
    oneLineSummary: 'Measure how much annual cash income you receive from dividends relative to the current share price.',
  },
  {
    slug: 'diluted_earnings_per_share_adjusted',
    shortLabel: 'Diluted EPS (Adjusted)',
    oneLineSummary: 'Track per-share earnings on a conservative, fully diluted and normalized basis.',
  },
  {
    slug: 'roa',
    shortLabel: 'Return on Assets (ROA)',
    oneLineSummary: 'Gauge how efficiently management turns the asset base into bottom-line profit.',
  },
  {
    slug: 'roe',
    shortLabel: 'Return on Equity (ROE)',
    oneLineSummary: 'See how effectively the company is compounding shareholders’ equity into profits.',
  },
  {
    slug: 'book_value_per_share',
    shortLabel: 'Book Value Per Share',
    oneLineSummary: 'Understand the accounting value of equity backing each share and how it changes over time.',
  },
  {
    slug: 'current_ratio',
    shortLabel: 'Current Ratio',
    oneLineSummary: 'Check whether current assets comfortably cover short-term liabilities.',
  },
  {
    slug: 'quick_ratio',
    shortLabel: 'Quick Ratio',
    oneLineSummary: 'Test short-term liquidity using only the most liquid assets, excluding inventory.',
  },
  {
    slug: 'operating_cash_flow',
    shortLabel: 'Operating Cash Flow',
    oneLineSummary: 'See how much cash the core business actually generates before financing and investing decisions.',
  },
  {
    slug: 'investing_cash_flow',
    shortLabel: 'Investing Cash Flow',
    oneLineSummary: 'Understand how much cash is being deployed into or recovered from long-term investments.',
  },
  {
    slug: 'financing_cash_flow',
    shortLabel: 'Financing Cash Flow',
    oneLineSummary: 'Track how debt, equity, dividends, and buybacks are changing the company’s capital structure.',
  },
  {
    slug: 'capital_expenditure',
    shortLabel: 'Capital Expenditure (CapEx)',
    oneLineSummary: 'See how much is being reinvested into long‑lived assets to maintain or grow the business.',
  },
  {
    slug: 'free_cash_flow',
    shortLabel: 'Free Cash Flow',
    oneLineSummary: 'Measure the cash left after required investments that can be used for dividends, buybacks, or debt reduction.',
  },
  {
    slug: 'cash_flow_margin',
    shortLabel: 'Cash Flow Margin',
    oneLineSummary: 'Compare operating cash flow to revenue to see how efficiently sales convert into cash.',
  },
  {
    slug: 'ocf_ratio',
    shortLabel: 'Operating Cash Flow Ratio',
    oneLineSummary: 'Compare operating cash flow to current liabilities to judge whether cash generation covers near‑term obligations.',
  },
  {
    slug: 'fcf_yield',
    shortLabel: 'Free Cash Flow Yield',
    oneLineSummary: 'Compare free cash flow to market cap to estimate the cash return you’re getting at the current share price.',
  },
];

const FundamentalIndicatorsTutorials: React.FC = () => {
  const { fontSize } = useFontSize();

  useEffect(() => {
    document.title = 'Stock Table Tutorials - Fundamental Indicators';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
      <Header />

      <main className="flex-grow max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-surface-light dark:bg-background-card rounded-lg shadow-lg p-6 sm:p-8 md:p-10">
          <header className="mb-8">
            <h1 className={`${getPageTitleSizeClass(fontSize)} font-bold text-text-light-primary dark:text-text-primary mb-2`}>
              Fundamental Indicator Tutorials
            </h1>
            <p className={`${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary max-w-3xl`}>
              Learn how each fundamental indicator is defined, calculated, and interpreted, and see exactly how Stock Table surfaces
              those signals so you can compare companies quickly.
            </p>
          </header>

          <section className="space-y-6">
            <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary`}>
              Explore Fundamental Indicators
            </h2>

            <div className="grid gap-6 md:grid-cols-2">
              {FUNDAMENTAL_TUTORIALS.map((indicator) => (
                <Link
                  key={indicator.slug}
                  to={`/tutorials/fundamentals/${indicator.slug}`}
                  className="group flex flex-col rounded-lg border border-border-light dark:border-border-color bg-surface-light dark:bg-background-card hover:border-primary/70 dark:hover:border-highlight-gold/70 hover:shadow-lg transition-all duration-200 p-4 sm:p-5"
                >
                  <div className="flex items-start gap-4">
                    <div className="mt-1 flex-shrink-0">
                      <FundamentalIndicatorVisual slug={indicator.slug} />
                    </div>

                    <div className="flex-1">
                      <h3 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary group-hover:text-gold-text-light dark:group-hover:text-highlight-gold`}>
                        {indicator.shortLabel}
                      </h3>
                      <p className={`${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary mt-1`}>
                        {indicator.oneLineSummary}
                      </p>
                      <p className={`${getTextSizeClass(fontSize)} text-gold-text-light dark:text-highlight-gold mt-3 font-medium`}>
                        Read tutorial →
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FundamentalIndicatorsTutorials;

